import { isApiError } from "@core/api/api-error";
import { ApiService } from "@core/api/api-service";

import { Program, ProgramContent, ProgramMetadata, ProgramUpdateData } from "@domain/program/program";
import {
	getProgramContentFromDto,
	getProgramLightContentDtoFromProgramContent,
	ProgramContentDto,
	ProgramLightContentDto,
} from "@domain/program/program-content-dto";
import { ProgramErrorCodes, ProgramNameTooLong } from "@domain/program/program-error";
import { ProgramStatus } from "@domain/program/program-status/program-status";
import { isNil } from "@utils/assert";
import { dateFromDateDto, dateToDateDto } from "@utils/date-utils";

export class ProgramService {
	constructor(private readonly apiService: ApiService) {}

	async loadPatientProgram(patientId: string, programId: string): Promise<Program> {
		const result = await this.apiService.get<ProgramDto>(`/patients/${patientId}/programs/${programId}`);
		return ProgramService.programFromDto(result.data, patientId);
	}

	async startNewDraftProgram(patientId: string, newProgram: ProgramContent): Promise<ProgramMetadata> {
		try {
			const newProgramDto = ProgramService.dtoFromProgramStartData(newProgram);
			const result = await this.apiService.post<ProgramMetadataDto>(
				`/patients/${patientId}/programs/start`,
				newProgramDto,
				undefined,
				() => false,
			);
			return ProgramService.metadataFromDto(result.data);
		} catch (e) {
			if (isApiError(e) && e.code === ProgramErrorCodes.ProgramNameTooLong) {
				throw new ProgramNameTooLong();
			}
			throw e;
		}
	}

	async startDraftProgram(
		patientId: string,
		programId: string,
		newProgram: ProgramContent,
	): Promise<ProgramMetadata> {
		const newProgramDto = ProgramService.dtoFromProgramStartData(newProgram);
		const result = await this.apiService.post<ProgramMetadataDto>(
			`/patients/${patientId}/programs/${programId}/start`,
			newProgramDto,
			undefined,
			() => false,
		);
		return ProgramService.metadataFromDto(result.data);
	}

	async createDraftProgram(
		patientId: string,
		draftProgram: ProgramContent,
		reportError?: () => boolean,
	): Promise<ProgramMetadata> {
		const newProgramDto = getProgramLightContentDtoFromProgramContent(draftProgram);
		const result = await this.apiService.post<ProgramMetadataDto>(
			`/patients/${patientId}/programs`,
			newProgramDto,
			undefined,
			reportError,
		);
		return ProgramService.metadataFromDto(result.data);
	}

	async updateProgram(
		patientId: string,
		programId: string,
		draftProgram: ProgramUpdateData,
		forceChanges = false,
	): Promise<ProgramMetadata> {
		const newProgramDto = ProgramService.dtoFromProgramUpdateData(draftProgram);
		const result = await this.apiService.post<ProgramMetadataDto>(
			`/patients/${patientId}/programs/${programId}`,
			newProgramDto,
			{ params: { forceChanges } },
			() => false,
		);
		return ProgramService.metadataFromDto(result.data);
	}

	async stopProgram(patientId: string, programId: string): Promise<ProgramMetadata> {
		const result = await this.apiService.post<ProgramMetadataDto>(`/patients/${patientId}/programs/${programId}/stop`);
		return ProgramService.metadataFromDto(result.data);
	}

	async deleteProgram(patientId: string, programId: string): Promise<void> {
		await this.apiService.delete(`/patients/${patientId}/programs/${programId}`);
	}

	// Dto Conversions
	private static dtoFromProgramStartData(programStartData: ProgramContent): ProgramStartDataDto {
		const { startDate, endDate, ...programContent } = programStartData;
		if (isNil(startDate) || isNil(endDate)) {
			throw new Error("dtoFromProgramStartData called with undefined programDates");
		}

		return {
			...getProgramLightContentDtoFromProgramContent(programContent),
			startDate: dateToDateDto(startDate),
			endDate: dateToDateDto(endDate),
		};
	}

	private static dtoFromProgramUpdateData(programUpdateData: ProgramUpdateData): ProgramUpdateDataDto {
		const { startDate, endDate, ...programContent } = programUpdateData;
		return {
			...getProgramLightContentDtoFromProgramContent(programContent),
			startDate: startDate ? dateToDateDto(startDate) : undefined,
			endDate: endDate ? dateToDateDto(endDate) : undefined,
		};
	}

	private static programFromDto(programDto: ProgramDto, patientId: string): Program {
		const { startDate, endDate, days, name, ...otherProps } = programDto;
		return {
			...otherProps,
			patientId,
			...getProgramContentFromDto({ days, name }),
			startDate: startDate ? dateFromDateDto(startDate) : undefined,
			endDate: endDate ? dateFromDateDto(endDate) : undefined,
		};
	}

	private static metadataFromDto(metadataDto: ProgramMetadataDto): ProgramMetadata {
		return {
			...metadataDto,
			startDate: metadataDto.startDate ? dateFromDateDto(metadataDto.startDate) : undefined,
			endDate: metadataDto.endDate ? dateFromDateDto(metadataDto.endDate) : undefined,
		};
	}
}

interface ProgramUpdateDataDto extends ProgramLightContentDto {
	startDate?: string;
	endDate?: string;
}

interface ProgramDto extends ProgramContentDto {
	id: string;
	startDate?: string;
	endDate?: string;
	status: ProgramStatus;
}

interface ProgramStartDataDto extends ProgramLightContentDto {
	startDate: string;
	endDate: string;
}

interface ProgramMetadataDto {
	id: string;
	status: ProgramStatus;
	startDate?: string;
	endDate?: string;
}
